import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import GridTable from '../../table/GridTable';
import ConfirmDialog from '../../modal/ConfirmDialog';
import { ColDef } from 'ag-grid-community';
import {
  usePurchaseLandingTrackingStore,
  IPurchaseLandingTrackingCode,
  IPurchaseLandingTrackingCodeSearch,
} from '../../../store/usePurchaseLandingTrackingStore';
import { useLoginStore } from '../../../store/useLogin';
import PurchaseLandingTrackingCodeModal from './PurchaseLandingTrackingCodeModal';
import PurchaseLandingManagementFilter from './filter/PurchaseLandingManagementFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode, faLink, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { preventWheelChange } from '../../../util/numberInput';

const SHARE_LINK_BASE =
  process.env.REACT_APP_PURCHASE_LANDING_SHARE_LINK_BASE ??
  'https://fortunelab.coredev.co.kr/purchase/';

const SCRIPT_URL =
  process.env.REACT_APP_EMBED_SCRIPT_URL ??
  'https://saju.coredev.co.kr/saju-embed.js';

const PurchaseLandingManagementPage = () => {
  const { codeList, codePage, fetchCodes, setSearch, deleteCodes, loading } =
    usePurchaseLandingTrackingStore();
  const { role } = useLoginStore();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<IPurchaseLandingTrackingCode | null>(null);
  const [isEmbedModalOpen, setEmbedModalOpen] = useState(false);
  const [embedCodeToCopy, setEmbedCodeToCopy] = useState('');
  const [embedWidth, setEmbedWidth] = useState('600');

  // 일괄 삭제용 그리드 ref / 모달 상태
  const gridRef = useRef<AgGridReact<IPurchaseLandingTrackingCode>>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);

  const rowSelection = useMemo(() => ({ mode: 'multiRow' as const }), []);

  const handleSelectionChanged = useCallback(() => {
    setSelectedCount(gridRef.current?.api.getSelectedRows().length ?? 0);
  }, []);

  const showDeleteModal = useCallback(() => {
    const selectedRows = gridRef.current?.api.getSelectedRows() ?? [];
    if (selectedRows.length === 0) {
      toast.warn('선택된 캠페인이 없습니다.');
      return;
    }
    setIsDeleteOpen(true);
  }, []);

  const onDeleteConfirm = useCallback(async () => {
    const selectedRows = gridRef.current?.api.getSelectedRows() ?? [];
    const ids = selectedRows.map(row => row.id).filter((id): id is number => typeof id === 'number');
    if (ids.length === 0) {
      toast.warn('선택된 캠페인이 없습니다.');
      setIsDeleteOpen(false);
      return;
    }
    const success = await deleteCodes(ids);
    setIsDeleteOpen(false);
    if (success) {
      gridRef.current?.api.deselectAll();
      setSelectedCount(0);
    }
  }, [deleteCodes]);

  const handleSearch = useCallback(
    (search: IPurchaseLandingTrackingCodeSearch) => {
      setSearch(search);
      fetchCodes(0);
    },
    [setSearch, fetchCodes]
  );

  const handleResetSearch = useCallback(() => {
    setSearch({});
    fetchCodes(0);
  }, [setSearch, fetchCodes]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handlePagination = useCallback(
    (page: number) => {
      fetchCodes(page);
    },
    [fetchCodes]
  );

  const copyShareLink = useCallback((trkId: string) => {
    const shareLink = `${SHARE_LINK_BASE}${trkId}`;
    navigator.clipboard.writeText(shareLink).then(
      () => toast.success('공유 링크가 복사되었습니다.'),
      () => toast.error('복사에 실패했습니다.')
    );
  }, []);

  const buildEmbedCode = useCallback(
    (trkId: string, width: string) =>
      `<saju-embed landing="purchase/${trkId}" width="${width}"></saju-embed>\n<script src="${SCRIPT_URL}"></script>`,
    []
  );

  const resetEmbedModalState = useCallback(() => {
    setEmbedModalOpen(false);
    setEmbedCodeToCopy('');
    setEmbedWidth('600');
    setSelectedCode(null);
  }, []);

  const handleOpenEmbedModal = useCallback((code: IPurchaseLandingTrackingCode) => {
    setSelectedCode(code);
    setEmbedWidth('600');
    setEmbedCodeToCopy(buildEmbedCode(code.trkId, '600'));
    setEmbedModalOpen(true);
  }, [buildEmbedCode]);

  const handleCopyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(embedCodeToCopy).then(
      () => {
        toast.success('임베드 코드가 복사되었습니다.');
        resetEmbedModalState();
      },
      () => toast.error('복사에 실패했습니다.')
    );
  }, [embedCodeToCopy, resetEmbedModalState]);

  useEffect(() => {
    if (!isEmbedModalOpen || !selectedCode) return;
    const normalizedWidth = (embedWidth || '').trim() || '600';
    setEmbedCodeToCopy(buildEmbedCode(selectedCode.trkId, normalizedWidth));
  }, [isEmbedModalOpen, selectedCode, embedWidth, buildEmbedCode]);

  const columnDefs = useMemo<ColDef<IPurchaseLandingTrackingCode>[]>(
    () => {
      const baseColumns: ColDef<IPurchaseLandingTrackingCode>[] = [
        {
          headerName: '캠페인명',
          field: 'campaignName',
          flex: 2,
          valueFormatter: params => params.value ?? '-',
        },
        {
          headerName: '유형',
          field: 'personType',
          width: 80,
          cellRenderer: (params: any) => {
            const personType = params.data?.personType;
            if (!personType) return '-';
            return (
              <span
                className={`!px-2 !py-0.5 !rounded !text-xs ${
                  personType === 1
                    ? '!bg-blue-100 !text-blue-800'
                    : '!bg-purple-100 !text-purple-800'
                }`}
              >
                {personType}인용
              </span>
            );
          },
        },
        {
          headerName: '패키지',
          flex: 2,
          cellRenderer: (params: any) => {
            const packages = params.data?.packages ?? [];
            if (packages.length === 0) return '-';
            return (
              <div className="!flex !flex-nowrap !gap-1 !items-center !h-full !overflow-hidden">
                {packages.map((pkg: any) => (
                  <span
                    key={pkg.id}
                    className="!px-2 !py-0.5 !rounded !text-xs !bg-gray-100 !text-gray-800 !whitespace-nowrap !flex-shrink-0"
                  >
                    {pkg.packageName} ({pkg.packagePrice.toLocaleString()}원)
                  </span>
                ))}
              </div>
            );
          },
        },
        {
          headerName: '공유 링크',
          flex: 2.5,
          cellRenderer: (params: any) => {
            const trkId = params.data?.trkId;
            if (!trkId) return null;
            const link = `${SHARE_LINK_BASE}${trkId}`;
            return (
              <div
                className="!flex !items-center !gap-2 !text-sm !h-full !w-full"
                style={{ minWidth: 0 }}
              >
                <span className="!flex-1 !truncate" title={link} style={{ minWidth: 0 }}>
                  {link}
                </span>
                <button
                  className="!text-blue-600 hover:!text-blue-800"
                  onClick={() => copyShareLink(trkId)}
                  title="공유 링크 복사"
                >
                  <FontAwesomeIcon icon={faLink} />
                </button>
              </div>
            );
          },
        },
      ];

      if (role === 'ROLE_ADMIN') {
        baseColumns.push({
          headerName: '가맹점',
          field: 'companyName',
          flex: 1.5,
          valueFormatter: params => params.value ?? '-',
        });
      }

      baseColumns.push(
        {
          headerName: '방문 수',
          field: 'landedUserCount',
          flex: 1,
          valueFormatter: params => params.value?.toLocaleString(),
        },
        {
          headerName: '결과 조회 수',
          field: 'resultUserCount',
          flex: 1,
          valueFormatter: params => params.value?.toLocaleString(),
        },
        {
          headerName: '전환율',
          flex: 1.1,
          valueFormatter: params => {
            const landed = params.data?.landedUserCount ?? 0;
            const result = params.data?.resultUserCount ?? 0;
            if (!landed) {
              return '-';
            }
            const rate = (result / landed) * 100;
            return `${rate.toFixed(1)}%`;
          },
        },
        {
          headerName: '입금',
          field: 'orderCreatedCount',
          flex: 1,
          valueFormatter: params => params.value?.toLocaleString(),
        },
        {
          headerName: '실전환율',
          flex: 1.1,
          valueFormatter: params => {
            const landed = params.data?.landedUserCount ?? 0;
            const created = params.data?.orderCreatedCount ?? 0;
            if (!landed) {
              return '-';
            }
            const rate = (created / landed) * 100;
            return `${rate.toFixed(1)}%`;
          },
        },
        {
          headerName: '생성일',
          field: 'createdAt',
          flex: 1.5,
          valueFormatter: params =>
            params.value ? new Date(params.value).toLocaleString() : '',
        },
        {
          headerName: '입력폼',
          width: 80,
          cellRenderer: (params: any) => {
            const code: IPurchaseLandingTrackingCode | undefined = params.data;
            if (!code?.trkId) return null;
            return (
              <button
                className="!text-blue-600 hover:!text-blue-800"
                onClick={() => handleOpenEmbedModal(code)}
                title="입력폼 퍼가기"
              >
                <FontAwesomeIcon icon={faCode} />
              </button>
            );
          },
        },
        {
          headerName: '관리',
          width: 80,
          cellRenderer: (params: any) => {
            const code: IPurchaseLandingTrackingCode | undefined = params.data;
            if (!code) return null;
            return (
              <button
                className="!text-blue-600 hover:!text-blue-800"
                onClick={() => {
                  setSelectedCode(code);
                  setEditModalOpen(true);
                }}
                title="캠페인 수정"
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
            );
          },
        }
      );

      return baseColumns;
    },
    [copyShareLink, handleOpenEmbedModal, role]
  );

  const visiblePageIndexes = useMemo(() => {
    if (!codePage) return [];
    const totalPages = codePage.totalPages;
    const currentPage = codePage.pageable.pageNumber;
    const maxVisible = 10;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);
    startPage = Math.max(0, endPage - maxVisible + 1);

    return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  }, [codePage]);

  return (
    <div className="!w-full !h-full !min-w-0 !overflow-hidden !flex !flex-col">
      <div className="!pt-3">
        <PurchaseLandingManagementFilter onSearch={handleSearch} onReset={handleResetSearch} />
      </div>
      {/* 생성 / 일괄 삭제 버튼 */}
      <div className="!py-3 !flex !items-center !justify-between">
        <button
          className="!flex !items-center !gap-1.5 !px-3 !py-1.5 !text-sm !rounded !bg-blue-600 !text-white hover:!bg-blue-700"
          onClick={() => setCreateModalOpen(true)}
        >
          <FontAwesomeIcon icon={faPlus} className="!text-xs" />
          랜딩페이지 생성
        </button>
        {selectedCount > 0 && (
          <button
            className="!flex !items-center !gap-1.5 !px-3 !py-1.5 !text-sm !rounded !bg-red-600 !text-white hover:!bg-red-700"
            onClick={showDeleteModal}
          >
            <FontAwesomeIcon icon={faTrash} className="!text-xs" />
            선택 삭제 ({selectedCount}건)
          </button>
        )}
      </div>

      {/* 테이블 */}
      <div className="!flex-1 !min-h-0 !min-w-0 !w-full !overflow-hidden">
        <GridTable<IPurchaseLandingTrackingCode>
          gridRef={gridRef}
          height="100%"
          rowData={codeList}
          columnDefs={columnDefs}
          isLoading={loading}
          rowSelection={rowSelection}
          onSelectionChanged={handleSelectionChanged}
        />
      </div>

      {/* 페이지네이션 */}
      {codePage && codePage.totalPages > 1 && (
        <div className="!w-full !mt-4 !overflow-x-auto !pb-1">
          <div className="!flex !justify-center !items-center !gap-2 !min-w-max">
            {visiblePageIndexes.map(index => (
              <button
                key={index}
                onClick={() => handlePagination(index)}
                className={`!px-3 !py-1 !rounded !text-sm ${
                  index === codePage.pageable.pageNumber
                    ? '!bg-blue-600 !text-white'
                    : 'hover:!bg-gray-100'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 임베드 코드 모달 */}
      {isEmbedModalOpen && (
        <div className="!fixed !inset-0 !bg-black !bg-opacity-50 !flex !items-center !justify-center !z-50">
          <div className="!bg-white !p-6 !rounded-lg !shadow-xl !w-full !max-w-lg">
            <h3 className="!text-lg !font-bold !mb-4">입력폼 코드 퍼가기</h3>
            <p className="!text-sm !mb-4 !text-gray-500">
              입력폼을 홈페이지나 HTML이 사용 가능한 블로그/카페 등에 삽입하여 사용할 수
              있습니다.
            </p>

            <div className="!mb-4">
              <label className="!text-sm !font-medium !text-gray-700">가로폭(px)</label>
              <input
                type="number"
                min="200"
                placeholder="예) 600"
                className="!w-full !p-2 !border !rounded !mt-1"
                value={embedWidth}
                onChange={event => setEmbedWidth(event.target.value)}
                onWheel={preventWheelChange}
              />
            </div>

            <textarea
              readOnly
              className="!w-full !h-24 !p-2 !border !rounded !bg-gray-50 !text-sm !resize-none !font-mono"
              value={embedCodeToCopy}
            />
            <div className="!flex !justify-end !gap-3 !mt-4">
              <button
                className="!px-4 !py-2 !rounded !bg-gray-200 hover:!bg-gray-300"
                onClick={resetEmbedModalState}
              >
                닫기
              </button>
              <button
                className="!px-4 !py-2 !rounded !bg-blue-600 !text-white hover:!bg-blue-700"
                onClick={handleCopyToClipboard}
              >
                복사
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 캠페인 모달 */}
      <PurchaseLandingTrackingCodeModal
        mode="create"
        isOpen={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
      <PurchaseLandingTrackingCodeModal
        mode="edit"
        isOpen={isEditModalOpen && !!selectedCode}
        code={selectedCode ?? undefined}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedCode(null);
        }}
        onSuccess={async () => {
          const currentPage = codePage?.pageable?.pageNumber ?? 0;
          await fetchCodes(currentPage);
        }}
      />

      {/* 캠페인 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={onDeleteConfirm}
        title="캠페인 삭제 확인"
        message={`선택한 ${selectedCount}건의 캠페인을 삭제하시겠습니까?
삭제된 캠페인은 사용자 화면에서 더이상 보이지 않습니다.`}
        confirmText="삭제"
        cancelText="취소"
        type="danger"
      />
    </div>
  );
};

export default PurchaseLandingManagementPage;
